import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import {
  useCurrentEntityId,
  useSelectedEntity,
} from '../../../hooks/useSelectedEntity/useSelectedEntity';
import { useClientSelection } from '../useClientSelection/useClientSelection';
import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { CSGetEntity, Entity } from '@admiin-com/ds-graphql';
import { useModal } from '../../components/ModalManager/ModalContext';

export function useEntityId() {
  const entityId = useCurrentEntityId();

  const { id } = useParams();
  const { pathname } = useLocation();
  const pathId = pathname.split('/').pop();

  const { modals } = useModal();
  const isSettingsModal = modals?.some((modal) => modal.type === 'settings');
  if (isSettingsModal) return entityId;

  if (pathname.includes('clients')) {
    return id || pathId;
  }

  return entityId;
}

export function useContactId() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const pathId = pathname.split('/').pop();

  if (pathname.includes('contacts')) {
    return id || pathId;
  }

  return null;
}

export const useEntity = () => {
  const id = useEntityId();

  const { data, loading } = useQuery(gql(CSGetEntity), {
    variables: {
      id,
    },
    skip: !id,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const entity: Entity = useMemo(() => data?.getEntity || {}, [data]);

  return {
    entity,
    loading,
  };
};

export function useEntityContext(isClientContext?: boolean) {
  // Get current entity from selected entity hook
  const {
    entity: selectedEntity,
    loading: selectedLoading,
    deleteEntity,
    refetch: selectedRefetch,
    ...restSelectedProps
  } = useSelectedEntity();

  // Get client entity from client selection hook
  const { selectedClient: clientEntity, loading: clientLoading } =
    useClientSelection();

  // Use client entity or selected entity based on context
  const entity = isClientContext ? clientEntity : selectedEntity;
  const loading = isClientContext ? clientLoading : selectedLoading;

  // Only provide deleteEntity in entity context, not client context
  const contextDeleteEntity = isClientContext ? undefined : deleteEntity;
  const refetch = isClientContext ? () => undefined : selectedRefetch;

  return {
    entity,
    loading,
    deleteEntity: contextDeleteEntity,
    refetch,
    ...restSelectedProps,
  };
}
